import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiRefreshCw, FiSave } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { readStoredUser } from "../utils/sessionUser";
import TaxSettings from "./TaxSettings";
import "./LegalEntitySettings.css";

export const LEGAL_ENTITY_FIELDS = Object.freeze([
  "hotel_name",
  "hotel_address_line_1",
  "hotel_address_line_2",
  "hotel_city",
  "hotel_region",
  "hotel_postal_code",
  "hotel_country_code",
  "hotel_email",
  "hotel_phone",
  "legal_name",
  "legal_trade_name",
  "tax_id_type",
  "tax_id",
  "registration_number",
  "legal_address_line_1",
  "legal_address_line_2",
  "legal_city",
  "legal_region",
  "legal_postal_code",
  "legal_country_code",
  "legal_email",
  "legal_phone",
]);

const EMPTY_FORM = Object.freeze(
  Object.fromEntries(LEGAL_ENTITY_FIELDS.map((field) => [field, ""]))
);

const COUNTRY_CODES = "AF AL DZ AD AO AR AM AU AT AZ BS BH BD BB BY BE BZ BJ BT BO BA BW BR BN BG BF BI KH CM CA CV CF TD CL CN CO KM CR HR CU CY CZ DK DJ DM DO EC EG SV EE SZ ET FJ FI FR GA GM GE DE GH GR GD GT GN GW GY HT HN HU IS IN ID IR IQ IE IL IT JM JP JO KZ KE KI KW KG LA LV LB LS LR LY LI LT LU MG MW MY MV ML MT MH MR MU MX FM MD MC MN ME MA MZ MM NA NR NP NL NZ NI NE NG KP MK NO OM PK PW PA PG PY PE PH PL PT QA RO RU RW KN LC VC WS SM ST SA SN RS SC SL SG SK SI SB SO ZA KR SS ES LK SD SR SE CH SY TW TJ TZ TH TL TG TO TT TN TR TM TV UG UA AE GB US UY UZ VU VA VE VN YE ZM ZW".split(" ");

const FIELD_LIMITS = Object.freeze({
  hotel_name: 255,
  hotel_address_line_1: 300,
  hotel_address_line_2: 300,
  hotel_city: 150,
  hotel_region: 150,
  hotel_postal_code: 32,
  hotel_email: 254,
  hotel_phone: 60,
  legal_name: 255,
  legal_trade_name: 255,
  tax_id_type: 50,
  tax_id: 120,
  registration_number: 120,
  legal_address_line_1: 300,
  legal_address_line_2: 300,
  legal_city: 150,
  legal_region: 150,
  legal_postal_code: 32,
  legal_email: 254,
  legal_phone: 60,
});

const COPY = Object.freeze({
  en: {
    eyebrow: "Settings",
    title: "Hotel and legal entity details",
    intro: "Manage the hotel's details, legal entity and tax configuration.",
    detailsTab: "Hotel and legal entity",
    taxesTab: "Taxes",
    settingsTabs: "Hotel settings sections",
    draftNotice: "These details are optional for now and do not change existing reservations, payments or hotel settings.",
    notConfigured: "No details have been saved for this Business Unit yet. The current hotel name is only a suggestion; select Save to confirm it.",
    hotelTitle: "Hotel details",
    hotelHelp: "The property name, contact details and physical address.",
    legalTitle: "Legal entity",
    legalHelp: "The company or person that legally operates this hotel.",
    currenciesTitle: "Configured currencies",
    baseCurrency: "Base currency",
    currenciesHelp: "Currencies and exchange rates are managed separately in Currencies.",
    currenciesLoading: "Loading currencies…",
    currenciesEmpty: "No currencies have been configured for this Business Unit yet.",
    currenciesError: "The configured currencies could not be loaded.",
    optional: "Optional",
    save: "Save details",
    saving: "Saving…",
    reset: "Discard changes",
    retry: "Retry",
    loading: "Loading hotel details…",
    switchingBusinessUnit: "Switching Business Unit…",
    saved: "Hotel and legal entity details saved.",
    loadError: "The hotel details could not be loaded.",
    saveError: "The hotel details could not be saved.",
    readOnly: "You can view these details, but you do not have permission to edit them.",
    invalidBu: "Select a valid Business Unit before opening these settings.",
    invalidCountry: "Use a two-letter ISO country code.",
    invalidEmail: "Enter a valid email address.",
    tooLong: "This value is too long.",
    noChanges: "There are no unsaved changes.",
    labels: {
      hotel_name: "Hotel name",
      hotel_address_line_1: "Address",
      hotel_address_line_2: "Address line 2",
      hotel_city: "City",
      hotel_region: "State / Region",
      hotel_postal_code: "Postal code",
      hotel_country_code: "Country",
      hotel_email: "Hotel email",
      hotel_phone: "Hotel phone",
      legal_name: "Legal name",
      legal_trade_name: "Legal trade name",
      tax_id_type: "Tax ID type",
      tax_id: "Tax ID",
      registration_number: "Registration number",
      legal_address_line_1: "Legal address",
      legal_address_line_2: "Legal address line 2",
      legal_city: "City",
      legal_region: "State / Region",
      legal_postal_code: "Postal code",
      legal_country_code: "Country",
      legal_email: "Legal entity email",
      legal_phone: "Legal entity phone",
    },
  },
  es: {
    eyebrow: "Ajustes",
    title: "Datos del hotel y entidad legal",
    intro: "Gestiona los datos del hotel, su entidad legal y la configuración de impuestos.",
    detailsTab: "Hotel y entidad legal",
    taxesTab: "Impuestos",
    settingsTabs: "Secciones de configuración del hotel",
    draftNotice: "Estos datos son opcionales por ahora y no modifican reservas, pagos ni ajustes existentes.",
    notConfigured: "Esta unidad de negocio todavía no tiene datos guardados. El nombre actual del hotel es solo una propuesta; pulsa Guardar para confirmarlo.",
    hotelTitle: "Datos del hotel",
    hotelHelp: "Nombre del establecimiento, contacto y dirección física.",
    legalTitle: "Entidad legal",
    legalHelp: "La empresa o persona que opera legalmente este hotel.",
    currenciesTitle: "Monedas configuradas",
    baseCurrency: "Moneda base",
    currenciesHelp: "Las monedas y los tipos de cambio se gestionan por separado en Monedas.",
    currenciesLoading: "Cargando monedas…",
    currenciesEmpty: "Esta unidad de negocio todavía no tiene monedas configuradas.",
    currenciesError: "No se han podido cargar las monedas configuradas.",
    optional: "Opcional",
    save: "Guardar datos",
    saving: "Guardando…",
    reset: "Descartar cambios",
    retry: "Reintentar",
    loading: "Cargando datos del hotel…",
    switchingBusinessUnit: "Cambiando de unidad de negocio…",
    saved: "Los datos del hotel y la entidad legal se han guardado.",
    loadError: "No se han podido cargar los datos del hotel.",
    saveError: "No se han podido guardar los datos del hotel.",
    readOnly: "Puedes consultar estos datos, pero no tienes permiso para modificarlos.",
    invalidBu: "Selecciona una unidad de negocio válida antes de abrir estos ajustes.",
    invalidCountry: "Utiliza un código de país ISO de dos letras.",
    invalidEmail: "Introduce una dirección de correo válida.",
    tooLong: "Este valor es demasiado largo.",
    noChanges: "No hay cambios pendientes.",
    labels: {
      hotel_name: "Nombre del hotel",
      hotel_address_line_1: "Dirección",
      hotel_address_line_2: "Dirección (línea 2)",
      hotel_city: "Ciudad",
      hotel_region: "Provincia / Región",
      hotel_postal_code: "Código postal",
      hotel_country_code: "País",
      hotel_email: "Email del hotel",
      hotel_phone: "Teléfono del hotel",
      legal_name: "Razón social",
      legal_trade_name: "Nombre comercial legal",
      tax_id_type: "Tipo de identificación fiscal",
      tax_id: "Identificación fiscal",
      registration_number: "Número de registro",
      legal_address_line_1: "Domicilio legal",
      legal_address_line_2: "Domicilio legal (línea 2)",
      legal_city: "Ciudad",
      legal_region: "Provincia / Región",
      legal_postal_code: "Código postal",
      legal_country_code: "País",
      legal_email: "Email de la entidad",
      legal_phone: "Teléfono de la entidad",
    },
  },
  ca: {
    eyebrow: "Ajustos",
    title: "Dades de l'hotel i entitat legal",
    intro: "Gestiona les dades de l'hotel, la seva entitat legal i la configuració d'impostos.",
    detailsTab: "Hotel i entitat legal",
    taxesTab: "Impostos",
    settingsTabs: "Seccions de configuració de l'hotel",
    draftNotice: "Aquestes dades són opcionals ara mateix i no modifiquen reserves, pagaments ni ajustos existents.",
    notConfigured: "Aquesta unitat de negoci encara no té dades desades. El nom actual de l'hotel és només una proposta; prem Desa per confirmar-lo.",
    hotelTitle: "Dades de l'hotel",
    hotelHelp: "Nom de l'establiment, contacte i adreça física.",
    legalTitle: "Entitat legal",
    legalHelp: "L'empresa o persona que opera legalment aquest hotel.",
    currenciesTitle: "Monedes configurades",
    baseCurrency: "Moneda base",
    currenciesHelp: "Les monedes i els tipus de canvi es gestionen per separat a Monedes.",
    currenciesLoading: "Carregant les monedes…",
    currenciesEmpty: "Aquesta unitat de negoci encara no té monedes configurades.",
    currenciesError: "No s'han pogut carregar les monedes configurades.",
    optional: "Opcional",
    save: "Desa les dades",
    saving: "Desant…",
    reset: "Descarta els canvis",
    retry: "Torna-ho a provar",
    loading: "Carregant les dades de l'hotel…",
    switchingBusinessUnit: "Canviant d'unitat de negoci…",
    saved: "Les dades de l'hotel i l'entitat legal s'han desat.",
    loadError: "No s'han pogut carregar les dades de l'hotel.",
    saveError: "No s'han pogut desar les dades de l'hotel.",
    readOnly: "Pots consultar aquestes dades, però no tens permís per modificar-les.",
    invalidBu: "Selecciona una unitat de negoci vàlida abans d'obrir aquests ajustos.",
    invalidCountry: "Utilitza un codi de país ISO de dues lletres.",
    invalidEmail: "Introdueix una adreça electrònica vàlida.",
    tooLong: "Aquest valor és massa llarg.",
    noChanges: "No hi ha canvis pendents.",
    labels: {
      hotel_name: "Nom de l'hotel",
      hotel_address_line_1: "Adreça",
      hotel_address_line_2: "Adreça (línia 2)",
      hotel_city: "Ciutat",
      hotel_region: "Província / Regió",
      hotel_postal_code: "Codi postal",
      hotel_country_code: "País",
      hotel_email: "Email de l'hotel",
      hotel_phone: "Telèfon de l'hotel",
      legal_name: "Raó social",
      legal_trade_name: "Nom comercial legal",
      tax_id_type: "Tipus d'identificació fiscal",
      tax_id: "Identificació fiscal",
      registration_number: "Número de registre",
      legal_address_line_1: "Domicili legal",
      legal_address_line_2: "Domicili legal (línia 2)",
      legal_city: "Ciutat",
      legal_region: "Província / Regió",
      legal_postal_code: "Codi postal",
      legal_country_code: "País",
      legal_email: "Email de l'entitat",
      legal_phone: "Telèfon de l'entitat",
    },
  },
});

const HOTEL_FIELDS = Object.freeze([
  "hotel_name",
  "hotel_email",
  "hotel_phone",
  "hotel_address_line_1",
  "hotel_address_line_2",
  "hotel_city",
  "hotel_region",
  "hotel_postal_code",
  "hotel_country_code",
]);

const LEGAL_FIELDS = Object.freeze([
  "legal_name",
  "legal_trade_name",
  "tax_id_type",
  "tax_id",
  "registration_number",
  "legal_email",
  "legal_phone",
  "legal_address_line_1",
  "legal_address_line_2",
  "legal_city",
  "legal_region",
  "legal_postal_code",
  "legal_country_code",
]);

const COUNTRY_FIELDS = new Set(["hotel_country_code", "legal_country_code"]);
const EMAIL_FIELDS = new Set(["hotel_email", "legal_email"]);

const normalizeForm = (source) =>
  Object.fromEntries(
    LEGAL_ENTITY_FIELDS.map((field) => [
      field,
      source?.[field] == null ? "" : String(source[field]),
    ])
  );

export const buildLegalEntityPayload = (form) =>
  Object.fromEntries(
    LEGAL_ENTITY_FIELDS.map((field) => {
      const value = String(form?.[field] ?? "").trim();
      return [field, value ? (COUNTRY_FIELDS.has(field) ? value.toUpperCase() : value) : null];
    })
  );

const payloadFingerprint = (form) => JSON.stringify(buildLegalEntityPayload(form));

export const validateLegalEntityForm = (form, copy = COPY.en) => {
  const errors = {};

  for (const field of LEGAL_ENTITY_FIELDS) {
    const value = String(form?.[field] ?? "").trim();
    const limit = FIELD_LIMITS[field];
    if (limit && value.length > limit) errors[field] = copy.tooLong;
  }

  for (const field of COUNTRY_FIELDS) {
    const value = String(form?.[field] ?? "").trim();
    if (value && !/^[A-Za-z]{2}$/.test(value)) errors[field] = copy.invalidCountry;
  }

  for (const field of EMAIL_FIELDS) {
    const value = String(form?.[field] ?? "").trim();
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors[field] = copy.invalidEmail;
    }
  }

  return errors;
};

const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

function LegalEntityField({ copy, disabled, errors, field, form, onChange }) {
  const error = errors[field];
  const inputId = `legal-entity-${field.replace(/_/g, "-")}`;
  const commonProps = {
    id: inputId,
    name: field,
    value: form[field],
    disabled,
    "aria-invalid": Boolean(error),
    "aria-describedby": error ? `${inputId}-error` : undefined,
    onChange: (event) => onChange(field, event.target.value),
  };

  return (
    <div className={`legalEntitySettings__field legalEntitySettings__field--${field.replace(/_/g, "-")}`}>
      <label htmlFor={inputId}>{copy.labels[field]}</label>
      {COUNTRY_FIELDS.has(field) ? (
        <input
          {...commonProps}
          type="text"
          maxLength={2}
          list="legal-entity-country-codes"
          placeholder="ISO"
          autoComplete="country"
        />
      ) : (
        <input
          {...commonProps}
          type={EMAIL_FIELDS.has(field) ? "email" : "text"}
          maxLength={FIELD_LIMITS[field]}
          autoComplete={field.includes("email") ? "email" : field.includes("phone") ? "tel" : "off"}
        />
      )}
      {error ? <span id={`${inputId}-error`} className="legalEntitySettings__fieldError">{error}</span> : null}
    </div>
  );
}

function LegalEntityCurrencies({ businessUnitId, copy, locale }) {
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    setConfiguration(null);
    axios.get("/api/currency-settings", {
      params: { business_unit_id: businessUnitId },
      signal: controller.signal,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (Number(data?.business_unit_id) !== businessUnitId) throw new Error("Currency BU mismatch");
      setConfiguration(data);
      setLoading(false);
    }).catch(() => {
      if (controller.signal.aborted) return;
      setFailed(true);
      setLoading(false);
    });
    return () => controller.abort();
  }, [businessUnitId, reloadToken]);

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: "currency" });
    } catch (_error) {
      return null;
    }
  }, [locale]);
  const baseCurrency = configuration?.base_currency_code;
  const currencies = [...new Set([
    baseCurrency,
    ...(Array.isArray(configuration?.currencies) ? configuration.currencies : []),
  ])].filter((code) => typeof code === "string" && /^[A-Z]{3}$/.test(code));

  return (
    <section className="legalEntitySettings__currencies" aria-labelledby="legal-entity-currencies-title">
      <h2 id="legal-entity-currencies-title">{copy.currenciesTitle}</h2>
      {loading ? <p role="status">{copy.currenciesLoading}</p> : failed ? (
        <div className="legalEntitySettings__currencyError" role="status">
          <span>{copy.currenciesError}</span>
          <button type="button" className="legalEntitySettings__button" onClick={() => setReloadToken((value) => value + 1)}>
            <FiRefreshCw aria-hidden="true" /> {copy.retry}
          </button>
        </div>
      ) : currencies.length ? (
        <ul className="legalEntitySettings__currencyList">
          {currencies.map((code) => (
            <li key={code} className={`legalEntitySettings__currency${code === baseCurrency ? " legalEntitySettings__currency--base" : ""}`}>
              <strong>{code}</strong>
              <span>{displayNames?.of(code) || code}</span>
              {code === baseCurrency ? <span className="legalEntitySettings__baseLabel">{copy.baseCurrency}</span> : null}
            </li>
          ))}
        </ul>
      ) : <p>{copy.currenciesEmpty}</p>}
      <p className="legalEntitySettings__currencyHelp">{copy.currenciesHelp}</p>
    </section>
  );
}

export default function LegalEntitySettings() {
  const [searchParams] = useSearchParams();
  const { languageId, language, themeId } = usePmsTheme();
  const copy = COPY[languageId] || COPY.en;
  const [currentUser, setCurrentUser] = useState(() => readStoredUser());
  const requestedBusinessUnitId = searchParams.get("business_unit_id") || "";
  const rawBusinessUnitId =
    requestedBusinessUnitId ||
    currentUser?.active_business_unit_id ||
    "";
  const businessUnitId = Number(rawBusinessUnitId);
  const businessUnitKey = Number.isInteger(businessUnitId) && businessUnitId > 0
    ? String(businessUnitId)
    : "";
  const permissions = currentUser?.permissions || {};
  const isSettingsAdmin = Number(currentUser?.id) === 1 || permissions.access_settings === true;
  const canManage =
    isSettingsAdmin ||
    permissions["finance.settings.manage_legal_entity"] === true;
  const canViewLegal = canManage || permissions["finance.settings.view"] === true;
  const canManageTaxes = isSettingsAdmin || permissions["finance.settings.manage_taxes"] === true;
  const canViewTaxes = canManageTaxes || permissions["finance.settings.view"] === true;
  const [selectedTab, setSelectedTab] = useState("details");
  const [taxesVisited, setTaxesVisited] = useState(false);
  const activeTab = canViewTaxes && (selectedTab === "taxes" || !canViewLegal) ? "taxes" : "details";
  const tabs = [
    ...(canViewLegal ? [{ key: "details", label: copy.detailsTab }] : []),
    ...(canViewTaxes ? [{ key: "taxes", label: copy.taxesTab }] : []),
  ];
  const selectTab = (tab) => {
    setSelectedTab(tab);
    if (tab === "taxes") setTaxesVisited(true);
  };
  const handleTabKeyDown = (event, index) => {
    const nextIndex = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1
      : event.key === "ArrowRight" ? (index + 1) % tabs.length
        : event.key === "ArrowLeft" ? (index - 1 + tabs.length) % tabs.length : null;
    if (nextIndex === null) return;
    event.preventDefault();
    selectTab(tabs[nextIndex].key);
    document.getElementById(`hotel-settings-tab-${tabs[nextIndex].key}`)?.focus();
  };
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [savedForm, setSavedForm] = useState(() => ({ ...EMPTY_FORM }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [loadedBusinessUnitKey, setLoadedBusinessUnitKey] = useState("");
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadGenerationRef = useRef(0);
  const saveGenerationRef = useRef(0);
  const activeBusinessUnitKeyRef = useRef(businessUnitKey);
  activeBusinessUnitKeyRef.current = businessUnitKey;
  const activeSessionBusinessUnitId = Number(currentUser?.active_business_unit_id || 0);
  const waitingForBusinessUnitSwitch =
    Boolean(requestedBusinessUnitId) &&
    Number.isInteger(activeSessionBusinessUnitId) &&
    activeSessionBusinessUnitId > 0 &&
    activeSessionBusinessUnitId !== businessUnitId;

  useEffect(() => {
    const syncSession = () => setCurrentUser(readStoredUser());
    window.addEventListener("palantir-session-updated", syncSession);
    return () => window.removeEventListener("palantir-session-updated", syncSession);
  }, []);

  const countryOptions = useMemo(() => {
    const locale = language?.locale || languageId || "en";
    let displayNames;
    try {
      displayNames = new Intl.DisplayNames([locale], { type: "region" });
    } catch (_error) {
      displayNames = null;
    }

    return COUNTRY_CODES
      .map((code) => ({ code, label: displayNames?.of(code) || code }))
      .sort((left, right) => left.label.localeCompare(right.label, locale));
  }, [language?.locale, languageId]);

  const isCurrentBusinessUnitLoaded = loadedBusinessUnitKey === businessUnitKey;
  const hasFieldEdits = payloadFingerprint(form) !== payloadFingerprint(savedForm);
  const hasUnsavedDefaults =
    !configured && Object.values(buildLegalEntityPayload(form)).some((value) => value !== null);
  const dirty = isCurrentBusinessUnitLoaded && (hasFieldEdits || hasUnsavedDefaults);

  useEffect(() => {
    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    saveGenerationRef.current += 1;
    setForm({ ...EMPTY_FORM });
    setSavedForm({ ...EMPTY_FORM });
    setErrors({});
    setFeedback(null);
    setConfigured(false);
    setLoadedBusinessUnitKey("");
    setSaving(false);

    if (!canViewLegal) {
      setLoading(false);
      return undefined;
    }

    if (!businessUnitKey) {
      setLoading(false);
      setFeedback({ tone: "error", message: copy.invalidBu });
      return undefined;
    }

    if (waitingForBusinessUnitSwitch) {
      setLoading(true);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);

    axios.get("/api/legal-entity", {
      params: { business_unit_id: businessUnitId },
      signal: controller.signal,
    }).then((response) => {
      if (
        generation !== loadGenerationRef.current ||
        activeBusinessUnitKeyRef.current !== businessUnitKey
      ) return;

      const entity = response?.data?.legal_entity || null;
      const nextForm = normalizeForm(entity || response?.data?.defaults);
      setForm(nextForm);
      setSavedForm(nextForm);
      setConfigured(response?.data?.configured === true || Boolean(entity));
      setLoadedBusinessUnitKey(businessUnitKey);
      setLoading(false);
    }).catch((error) => {
      if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
      if (
        generation !== loadGenerationRef.current ||
        activeBusinessUnitKeyRef.current !== businessUnitKey
      ) return;

      setLoadedBusinessUnitKey("");
      setLoading(false);
      setFeedback({ tone: "error", message: apiErrorMessage(error, copy.loadError), retry: true });
    });

    return () => controller.abort();
  }, [
    businessUnitId,
    businessUnitKey,
    canViewLegal,
    copy.invalidBu,
    copy.loadError,
    reloadToken,
    waitingForBusinessUnitSwitch,
  ]);

  const handleChange = (field, rawValue) => {
    const value = COUNTRY_FIELDS.has(field) ? rawValue.toUpperCase() : rawValue;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setFeedback(null);
  };

  const handleReset = () => {
    setForm({ ...savedForm });
    setErrors({});
    setFeedback(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canManage || saving || !dirty || !isCurrentBusinessUnitLoaded) return;

    const validationErrors = validateLegalEntityForm(form, copy);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      const firstField = Object.keys(validationErrors)[0];
      document.getElementById(`legal-entity-${firstField.replace(/_/g, "-")}`)?.focus();
      return;
    }

    const targetBusinessUnitKey = businessUnitKey;
    const generation = saveGenerationRef.current + 1;
    saveGenerationRef.current = generation;
    setSaving(true);
    setFeedback(null);

    try {
      const response = await axios.put(
        `/api/legal-entity/${businessUnitId}`,
        buildLegalEntityPayload(form)
      );
      if (
        generation !== saveGenerationRef.current ||
        activeBusinessUnitKeyRef.current !== targetBusinessUnitKey
      ) return;

      const nextForm = normalizeForm(response?.data?.legal_entity || buildLegalEntityPayload(form));
      setForm(nextForm);
      setSavedForm(nextForm);
      setConfigured(true);
      setErrors({});
      setFeedback({ tone: "success", message: copy.saved });
    } catch (error) {
      if (
        generation !== saveGenerationRef.current ||
        activeBusinessUnitKeyRef.current !== targetBusinessUnitKey
      ) return;
      setFeedback({ tone: "error", message: apiErrorMessage(error, copy.saveError) });
    } finally {
      if (
        generation === saveGenerationRef.current &&
        activeBusinessUnitKeyRef.current === targetBusinessUnitKey
      ) setSaving(false);
    }
  };

  const renderFields = (fields) => fields.map((field) => (
    <LegalEntityField
      key={field}
      copy={copy}
      disabled={loading || saving || !canManage || !isCurrentBusinessUnitLoaded}
      errors={errors}
      field={field}
      form={form}
      onChange={handleChange}
    />
  ));

  return (
    <div className="legalEntitySettings" data-theme={themeId === "light" ? "light" : "dark"}>
      <section className="legalEntitySettings__shell" aria-busy={loading || saving}>
        <header className="legalEntitySettings__header">
          <div>
            <p className="legalEntitySettings__eyebrow">{copy.eyebrow}</p>
            <h1>{copy.title}</h1>
            <p>{copy.intro}</p>
          </div>
          <span className="legalEntitySettings__optional">{copy.optional}</span>
        </header>

        <div className="legalEntitySettings__tabs" role="tablist" aria-label={copy.settingsTabs}>
          {tabs.map((tab, index) => (
            <button
              type="button"
              role="tab"
              key={tab.key}
              id={`hotel-settings-tab-${tab.key}`}
              aria-controls={`hotel-settings-panel-${tab.key}`}
              aria-selected={activeTab === tab.key}
              tabIndex={activeTab === tab.key ? 0 : -1}
              onClick={() => selectTab(tab.key)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {canViewLegal ? (
        <div id="hotel-settings-panel-details" role="tabpanel" aria-labelledby="hotel-settings-tab-details" hidden={activeTab !== "details"}>
        <div className="legalEntitySettings__notice" role="note">
          <FiAlertCircle aria-hidden="true" />
          <span>{copy.draftNotice}</span>
        </div>

        {businessUnitKey && !waitingForBusinessUnitSwitch ? (
          <LegalEntityCurrencies
            key={businessUnitKey}
            businessUnitId={businessUnitId}
            copy={copy}
            locale={language?.locale || languageId || "en"}
          />
        ) : null}

        {!canManage ? (
          <div className="legalEntitySettings__feedback legalEntitySettings__feedback--info" role="status">
            {copy.readOnly}
          </div>
        ) : null}

        {feedback ? (
          <div
            className={`legalEntitySettings__feedback legalEntitySettings__feedback--${feedback.tone}`}
            role={feedback.tone === "error" ? "alert" : "status"}
          >
            {feedback.tone === "success" ? <FiCheckCircle aria-hidden="true" /> : <FiAlertCircle aria-hidden="true" />}
            <span>{feedback.message}</span>
            {feedback.retry && businessUnitKey ? (
              <button type="button" onClick={() => setReloadToken((value) => value + 1)}>
                <FiRefreshCw aria-hidden="true" /> {copy.retry}
              </button>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <div className="legalEntitySettings__loading" role="status">
            {waitingForBusinessUnitSwitch ? copy.switchingBusinessUnit : copy.loading}
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <datalist id="legal-entity-country-codes">
              {countryOptions.map(({ code, label }) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </datalist>
            {!configured && isCurrentBusinessUnitLoaded ? (
              <p className="legalEntitySettings__empty">{copy.notConfigured}</p>
            ) : null}

            <div className="legalEntitySettings__sections">
              <fieldset disabled={saving || !canManage || !isCurrentBusinessUnitLoaded}>
                <legend>{copy.hotelTitle}</legend>
                <p>{copy.hotelHelp}</p>
                <div className="legalEntitySettings__grid">{renderFields(HOTEL_FIELDS)}</div>
              </fieldset>

              <fieldset disabled={saving || !canManage || !isCurrentBusinessUnitLoaded}>
                <legend>{copy.legalTitle}</legend>
                <p>{copy.legalHelp}</p>
                <div className="legalEntitySettings__grid">{renderFields(LEGAL_FIELDS)}</div>
              </fieldset>
            </div>

            <footer className="legalEntitySettings__actions">
              <span aria-live="polite">{!dirty && isCurrentBusinessUnitLoaded ? copy.noChanges : ""}</span>
              <button
                type="button"
                className="legalEntitySettings__button"
                onClick={handleReset}
                disabled={!hasFieldEdits || saving || !canManage}
              >
                {copy.reset}
              </button>
              <button
                type="submit"
                className="legalEntitySettings__button legalEntitySettings__button--primary"
                disabled={!dirty || saving || !canManage || !isCurrentBusinessUnitLoaded}
              >
                <FiSave aria-hidden="true" /> {saving ? copy.saving : copy.save}
              </button>
            </footer>
          </form>
        )}
        </div>
        ) : null}

        {canViewTaxes ? (
          <div id="hotel-settings-panel-taxes" role="tabpanel" aria-labelledby="hotel-settings-tab-taxes" hidden={activeTab !== "taxes"}>
            {!businessUnitKey || waitingForBusinessUnitSwitch ? (
              <p className="legalEntitySettings__loading" role="status">
                {waitingForBusinessUnitSwitch ? copy.switchingBusinessUnit : copy.invalidBu}
              </p>
            ) : taxesVisited || activeTab === "taxes" ? (
              <TaxSettings key={businessUnitKey} businessUnitId={businessUnitId} canManage={canManageTaxes} />
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
